import React, { useEffect, useState, useCallback } from "react";
import {
  Tabs,
  Button,
  Select,
  Input,
  Empty,
  Row,
  Col,
  Card,
  Popconfirm,
  message,
  Spin,
} from "antd";
import {
  OrderedListOutlined,
  PlusOutlined,
  DeleteOutlined,
  RollbackOutlined,
  SaveOutlined,
  MenuOutlined,
} from "@ant-design/icons";
import RequestHandler from "../../util/RequestHandler";
import { useHistory } from "react-router-dom";

const { TabPane } = Tabs;
const { Option } = Select;

const MODULE_TYPES = {
  PurchaseRequest: 0,
  Quote: 1,
  Order: 2,
};

const TAB_LABELS = {
  [MODULE_TYPES.PurchaseRequest]: "Talep",
  [MODULE_TYPES.Quote]: "Teklif",
  [MODULE_TYPES.Order]: "Sipariş",
};

const FirmApprovalAssignmentList = (props) => {
  const [activeTab, setActiveTab] = useState("0");
  const [stages, setStages] = useState({ 0: [], 1: [], 2: [] });
  const [loading, setLoading] = useState({ 0: true, 1: true, 2: true });
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const firmId = props.match.params.firmId;
  const firmName = props.match.params.firmName ?? "";

  const history = useHistory();

  useEffect(() => {
    loadUsers();
    loadStages(0);
    loadStages(1);
    loadStages(2);
  }, [firmId]);

  const loadUsers = () => {
    RequestHandler.get(`/TenantUser?$count=true&$skip=0&$top=500`).then(
      (response) => {
        const list = (response.value || [])
          .filter((u) => u.isActive && !u.isDeleted)
          .map((u) => ({
            id: u.id,
            name: `${u.firstName || ""} ${u.lastName || ""}`.trim(),
            email: u.email || "",
          }));
        setUsers(list);
      }
    );
  };

  const loadStages = (moduleType) => {
    setLoading((prev) => ({ ...prev, [moduleType]: true }));
    RequestHandler.get(
      `/ProjectApprovalFlowStage/${firmId}/${moduleType}`
    ).then((response) => {
      setStages((prev) => ({
        ...prev,
        [moduleType]: response || [],
      }));
      setLoading((prev) => ({ ...prev, [moduleType]: false }));
    });
  };

  const addStage = (moduleType) => {
    const currentStages = stages[moduleType];
    const newOrder =
      currentStages.length > 0
        ? Math.max(...currentStages.map((s) => s.stageOrder)) + 1
        : 1;
    setStages((prev) => ({
      ...prev,
      [moduleType]: [
        ...prev[moduleType],
        {
          id: null,
          stageOrder: newOrder,
          tenantUserId: null,
          title: "",
          isNew: true,
        },
      ],
    }));
  };

  const removeStage = (moduleType, index) => {
    setStages((prev) => ({
      ...prev,
      [moduleType]: prev[moduleType]
        .filter((_, i) => i !== index)
        .map((s, i) => ({ ...s, stageOrder: i + 1 })),
    }));
  };

  const updateStageField = (moduleType, index, field, value) => {
    setStages((prev) => ({
      ...prev,
      [moduleType]: prev[moduleType].map((s, i) =>
        i === index ? { ...s, [field]: value } : s
      ),
    }));
  };

  const handleSave = async (moduleType) => {
    const currentStages = stages[moduleType];

    for (let s of currentStages) {
      if (!s.tenantUserId) {
        message.error("Tüm adımlar için kullanıcı seçilmelidir.");
        return;
      }
      if (!s.title || s.title.trim() === "") {
        message.error("Tüm adımlar için başlık girilmelidir.");
        return;
      }
    }

    setSaving(true);
    const payload = {
      firmId: firmId,
      moduleType: moduleType,
      stages: currentStages.map((s, i) => ({
        id: s.id && !s.isNew ? s.id : null,
        tenantUserId: s.tenantUserId,
        stageOrder: i + 1,
        title: s.title,
      })),
    };

    try {
      const res = await RequestHandler.post(
        `/ProjectApprovalFlowStage/BulkSave`,
        payload
      );
      if (res.success) {
        message.success("Onay akışı kaydedildi.");
        loadStages(moduleType);
      } else {
        message.error(res.message || "Kayıt sırasında hata oluştu.");
      }
    } catch (err) {
      message.error("Kayıt sırasında hata oluştu.");
    }
    setSaving(false);
  };

  const handleDragStart = (index) => {
    setDragIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
  };

  const handleDrop = (moduleType, dropIndex) => {
    if (dragIndex === null || dragIndex === dropIndex) return;
    const currentStages = [...stages[moduleType]];
    const [removed] = currentStages.splice(dragIndex, 1);
    currentStages.splice(dropIndex, 0, removed);
    const reordered = currentStages.map((s, i) => ({
      ...s,
      stageOrder: i + 1,
    }));
    setStages((prev) => ({ ...prev, [moduleType]: reordered }));
    setDragIndex(null);
  };

  const renderStageList = (moduleType) => {
    const currentStages = stages[moduleType];
    const isLoading = loading[moduleType];

    if (isLoading) {
      return (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      );
    }

    return (
      <div>
        {currentStages.length === 0 && (
          <Empty
            description="Henüz onay adımı tanımlanmamış. Bu durumda kayıtlar otomatik onaylanır."
            style={{ margin: "30px 0" }}
          />
        )}

        {currentStages.map((stage, index) => (
          <Card
            key={stage.id || `new-${index}`}
            size="small"
            style={{
              marginBottom: 8,
              border: "1px solid #d9d9d9",
              cursor: "grab",
            }}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDrop={() => handleDrop(moduleType, index)}
          >
            <Row gutter={12} align="middle">
              <Col flex="30px">
                <MenuOutlined
                  style={{ color: "#999", fontSize: 16, cursor: "grab" }}
                />
              </Col>
              <Col flex="30px">
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: 16,
                    color: "#1890ff",
                  }}
                >
                  {index + 1}.
                </span>
              </Col>
              <Col flex="auto" style={{ minWidth: 200 }}>
                <Select
                  showSearch
                  placeholder="Kullanıcı seçin"
                  value={stage.tenantUserId}
                  onChange={(val) =>
                    updateStageField(moduleType, index, "tenantUserId", val)
                  }
                  style={{ width: "100%" }}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {users.map((u) => (
                    <Option key={u.id} value={u.id}>
                      {u.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col flex="auto" style={{ minWidth: 180 }}>
                <Input
                  placeholder="Başlık (örn: Talepçi, 2. Onaycı)"
                  value={stage.title}
                  onChange={(e) =>
                    updateStageField(
                      moduleType,
                      index,
                      "title",
                      e.target.value
                    )
                  }
                />
              </Col>
              <Col flex="40px">
                <Popconfirm
                  title="Bu adımı kaldırmak istiyor musunuz?"
                  okText="Evet"
                  cancelText="Hayır"
                  onConfirm={() => removeStage(moduleType, index)}
                >
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                  />
                </Popconfirm>
              </Col>
            </Row>
          </Card>
        ))}

        <Row style={{ marginTop: 16 }} gutter={12}>
          <Col>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => addStage(moduleType)}
            >
              Yeni Adım Ekle
            </Button>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={() => handleSave(moduleType)}
            >
              Kaydet
            </Button>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <>
      <Row style={{ marginBottom: 9 }}>
        <Col flex={1}>
          <a
            onClick={() => history.goBack()}
            style={{ color: "black", marginLeft: "4%" }}
          >
            <RollbackOutlined /> GERİ DÖN
          </a>
        </Col>
        <Col flex={4}></Col>
      </Row>
      <Row>
        <Col xs={24} md={16} lg={18}>
          <p style={{ fontSize: "120%", marginTop: -5 }}>
            <OrderedListOutlined /> PROJE ONAY AKIŞI YÖNETİMİ
          </p>
        </Col>
        <Col xs={24} md={8} lg={6} style={{ textAlign: "end", fontSize: 15 }}>
          <p style={{ fontWeight: "bold", color: "#2c3e50" }}>
            <span style={{ color: "#6f5196" }}>{firmName}</span>
          </p>
        </Col>
      </Row>

      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab} type="card">
          {Object.keys(TAB_LABELS).map((key) => (
            <TabPane tab={TAB_LABELS[key]} key={key}>
              {renderStageList(parseInt(key))}
            </TabPane>
          ))}
        </Tabs>
      </Card>
    </>
  );
};

export default FirmApprovalAssignmentList;
